import React, { createContext, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { Toaster } from 'react-hot-toast';

// Pages
import Home from './pages/Home';
import ScamList from './pages/ScamList';
import ReportForm from './pages/ReportForm';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import EscrowPage from './pages/EscrowPage';
import CertifiedSellers from './pages/CertifiedSellers';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Create a global app context
export const AppContext = createContext();

// Theme
const theme = {
  colors: {
    primary: '#6c5ce7',       // Violet principal
    primaryDark: '#5849c2',   // Violet foncé
    primaryLight: '#a29bfe',  // Violet clair
    secondary: '#00b894',     // Vert menthe
    secondaryDark: '#00a884', // Vert menthe foncé
    accent: '#fd79a8',        // Rose accent
    accentDark: '#e84393',    // Rose foncé
    gradient1: 'linear-gradient(to right, #6c5ce7, #a29bfe)',
    gradient2: 'linear-gradient(to right, #00b894, #55efc4)',
    gradient3: 'linear-gradient(to right, #fd79a8, #fab1a0)',
    darkBg: '#121212',        // Fond noir principal
    darkBg2: '#1E1E1E',       // Fond noir secondaire
    darkBg3: '#252525',       // Fond noir tertiaire
    cardDark: '#1A1A1A',      // Cartes sur fond sombre
    text: '#E4E6EB',          // Texte principal sur fond sombre
    textMuted: '#B0B3B8',     // Texte secondaire sur fond sombre
    white: '#ffffff',
    error: '#ff5252',
    success: '#00b894',
    border: '#333333',        // Bordures sur fond sombre
  },
  fonts: {
    main: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
    headings: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  breakpoints: {
    mobile: '768px',
  },
  boxShadow: {
    small: '0 4px 6px rgba(0, 0, 0, 0.3)',
    medium: '0 6px 12px rgba(0, 0, 0, 0.4)',
    large: '0 10px 20px rgba(0, 0, 0, 0.5)',
    hover: '0 14px 28px rgba(0, 0, 0, 0.6)',
    button: '0 4px 12px rgba(108, 92, 231, 0.3)',
  },
  transition: {
    default: 'all 0.3s ease',
    fast: 'all 0.2s ease',
    slow: 'all 0.5s ease',
  }
};

// Global styles
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Outfit:wght@300;400;500;600;700&display=swap');

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  body {
    font-family: ${props => props.theme.fonts.main};
    background-color: ${props => props.theme.colors.darkBg};
    color: ${props => props.theme.colors.text};
    line-height: 1.6;
    overflow-x: hidden;
    font-weight: 400;
    letter-spacing: 0.015em;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  a {
    text-decoration: none;
    color: ${props => props.theme.colors.primary};
    transition: ${props => props.theme.transition.default};
    
    &:hover {
      color: ${props => props.theme.colors.primaryLight};
    }
  }

  button {
    cursor: pointer;
    border: none;
    outline: none;
    border-radius: 8px;
    transition: ${props => props.theme.transition.default};
    font-family: ${props => props.theme.fonts.main};
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${props => props.theme.boxShadow.hover};
    }
    
    &:active {
      transform: translateY(0);
    }
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.fonts.headings};
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.3;
  }
  
  p {
    margin-bottom: 1rem;
    font-size: 1.05rem;
    opacity: 0.95;
    line-height: 1.7;
  }
  
  input, textarea, select {
    border-radius: 8px;
    border: 1px solid ${props => props.theme.colors.border};
    padding: 12px 16px;
    font-family: ${props => props.theme.fonts.main};
    font-size: 16px;
    transition: ${props => props.theme.transition.default};
    background-color: ${props => props.theme.colors.darkBg3};
    color: ${props => props.theme.colors.text};
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
      box-shadow: 0 0 0 3px rgba(108, 92, 231, 0.2);
    }
    
    &::placeholder {
      color: ${props => props.theme.colors.textMuted};
      opacity: 0.7;
    }
  }
  
  ::selection {
    background-color: rgba(108, 92, 231, 0.4);
    color: ${props => props.theme.colors.white};
  }

  /* Scrollbar customization */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.darkBg3};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primaryDark};
    border-radius: 4px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.primary};
  }
`;

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.darkBg};
  overflow-x: hidden;
`;

const Main = styled.main`
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1.25rem 1rem;
  }
`;

function App() {
  // État global de l'application
  const [appState, setAppState] = useState({
    apiConnected: false,
    lastApiCheck: null,
    error: null
  });

  // Vérifier l'état de l'API au chargement de l'application
  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        console.log('Checking API status...');
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 seconds timeout
        
        // Ajouter un timestamp pour éviter le cache
        const timestamp = new Date().getTime();
        const apiUrl = process.env.REACT_APP_API_URL || 'https://holyhubs.com/api';
        const response = await fetch(`${apiUrl}/status?_t=${timestamp}`, {
          signal: controller.signal,
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        
        const data = await response.json();
        console.log('API Status response:', data);
        
        // Force une actualisation de l'interface si l'état a changé
        const wasConnected = appState.apiConnected;
        const isNowConnected = data.status === 'ok' && data.mongoStatus === 'Connected';
        
        setAppState(prev => ({
          ...prev,
          apiConnected: isNowConnected,
          mongoStatus: data.mongoStatus,
          lastApiCheck: new Date(),
          error: isNowConnected ? null : 'MongoDB non connecté'
        }));
        
        console.log('API Status updated:', { 
          wasConnected, 
          isNowConnected, 
          change: wasConnected !== isNowConnected,
          mongoStatus: data.mongoStatus
        });
      } catch (error) {
        console.error('API Status check failed:', error);
        
        setAppState(prev => ({
          ...prev,
          apiConnected: false,
          mongoStatus: 'Disconnected',
          lastApiCheck: new Date(),
          error: error.message
        }));
      }
    };
    
    // Vérifier immédiatement
    checkApiStatus();
    
    // Puis vérifier régulièrement
    const intervalId = setInterval(checkApiStatus, 30000); // Vérifier toutes les 30 secondes
    
    return () => clearInterval(intervalId);
  }, [appState.apiConnected]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AppContext.Provider value={{ appState, setAppState }}>
        <Router>
          <AppContainer>
            <Navbar apiConnected={appState.apiConnected} />
            <Main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/scam-list" element={<ScamList />} />
                <Route path="/report" element={<ReportForm />} />
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin-dashboard" element={<AdminDashboard />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/escrow" element={<EscrowPage />} />
                <Route path="/certified-sellers" element={<CertifiedSellers />} />
              </Routes>
            </Main>
            <Footer />
          </AppContainer>
        </Router>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: theme.colors.darkBg2,
              color: theme.colors.text,
              border: `1px solid ${theme.colors.border}`,
            },
            success: {
              iconTheme: {
                primary: theme.colors.success,
                secondary: theme.colors.white,
              },
              style: {
                border: `1px solid ${theme.colors.success}`,
              },
            },
            error: {
              iconTheme: {
                primary: theme.colors.error,
                secondary: theme.colors.white,
              },
              style: {
                border: `1px solid ${theme.colors.error}`,
              },
            },
            duration: 3000,
          }}
        />
      </AppContext.Provider>
    </ThemeProvider>
  );
}

export default App;
